import { HTMLAttributes, useCallback, useState } from 'react'
import { useKey } from 'react-use'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import Modal from '@/components/base/Modal'
import Section from '@/components/base/Section'
import SVGIcon from '@/components/base/SVGIcon'

import { cn } from '@/lib/utils'
import { WPImage, WPPageData } from '@/types'

export type GalleryGridProps = HTMLAttributes<HTMLElement> &
  Partial<{
    theme: WPPageData['acf']['theme']
    items: { image: WPImage }[]
  }>

const GalleryGrid = ({ items = [], theme, ...props }: GalleryGridProps) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isOpen, setIsOpen] = useState(false)

  const openLightbox = useCallback((index: number) => {
    setActiveIndex(index)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => setIsOpen(false), [])

  const showPrev = useCallback(
    () => setActiveIndex((index) => (index - 1 + items.length) % items.length),
    [items.length]
  )

  const showNext = useCallback(
    () => setActiveIndex((index) => (index + 1) % items.length),
    [items.length]
  )

  useKey('Escape', close, { event: 'keydown' }, [close])
  useKey('ArrowLeft', showPrev, { event: 'keydown' }, [showPrev])
  useKey('ArrowRight', showNext, { event: 'keydown' }, [showNext])

  if (!items.length) return null

  return (
    <Section
      {...props}
      className={cn([
        {
          'bg-background text-foreground': theme === 'light',
          'bg-black text-white': theme === 'dark'
        }
      ])}
    >
      <Container>
        <Grid className="gap-y-[2rem] md:gap-y-[3rem]">
          <div className="col-span-4 md:col-span-6 lg:col-span-10 lg:col-start-2">
            <div className="grid grid-cols-3 gap-[0.2rem]">
              {items.map(({ image }, index) => (
                <button
                  key={`gallery-image-${index}`}
                  type="button"
                  onClick={() => openLightbox(index)}
                  className="relative aspect-[4/5] w-full overflow-hidden"
                >
                  <Image source={image} alt={image?.alt} fill fit="cover" />
                </button>
              ))}
            </div>
          </div>
        </Grid>
      </Container>

      <Modal open={isOpen}>
        <div
          className={cn(
            'fixed left-0 top-0 z-modal flex h-dvh w-full items-center justify-center bg-black',
            { 'pointer-events-auto': isOpen }
          )}
        >
          <div className="absolute inset-[6%]">
            <Image
              source={items[activeIndex]?.image}
              alt={items[activeIndex]?.image?.alt}
              fill
              fit="contain"
            />
          </div>

          <button
            className="absolute right-[2rem] top-[2rem] z-20 flex p-[1rem]"
            onClick={close}
          >
            <span className="sr-only">Close</span>
            <SVGIcon
              symbol="close-icon"
              className="h-[1.4rem] w-[1.4rem] fill-white"
            />
          </button>

          {items.length > 1 ? (
            <>
              <button
                className="absolute left-[2rem] top-1/2 z-20 flex -translate-y-1/2 p-[1rem]"
                onClick={showPrev}
              >
                <span className="sr-only">Previous image</span>
                <SVGIcon
                  symbol="prev-icon"
                  className="h-[1.3rem] w-[0.7rem] fill-white"
                />
              </button>
              <button
                className="absolute right-[2rem] top-1/2 z-20 flex -translate-y-1/2 p-[1rem]"
                onClick={showNext}
              >
                <span className="sr-only">Next image</span>
                <SVGIcon
                  symbol="next-icon"
                  className="h-[1.3rem] w-[0.7rem] fill-white"
                />
              </button>
            </>
          ) : null}
        </div>
      </Modal>
    </Section>
  )
}

export default GalleryGrid
